import { createInjunctionDecision } from "@/api/cle-api";
import {
  createPersonPaalaeg,
  deletePersonPaalaeg,
  getPersonPaalaeg,
  getPersonPaalaegList,
  patchPersonPaalaeg,
  type CreatePersonPaalaegBody,
  type PersonPaalaegDTO,
  type UpdatePersonPaalaegFields,
} from "@/api/person/paalaeg";
import { getPatchOperations } from "@/util/form";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

export interface UsePersonPaalaegListParams {
  personId?: string;
}

export interface UsePersonPaalaegParams {
  personId?: string;
  paalaegId?: string;
}

export const personPaalaegListQueryKey = (personId: string) =>
  ["personPaalaeg", personId] as const;

export const personPaalaegQueryKey = (personId: string, paalaegId: string) =>
  ["personPaalaeg", personId, paalaegId] as const;

export const usePersonPaalaegList = ({
  personId,
}: UsePersonPaalaegListParams): UseQueryResult<PersonPaalaegDTO[], Error> =>
  useQuery({
    queryKey: personPaalaegListQueryKey(personId ?? ""),
    queryFn: () => getPersonPaalaegList(personId ?? ""),
    enabled: Boolean(personId),
  });

export const usePersonPaalaeg = ({
  personId,
  paalaegId,
}: UsePersonPaalaegParams): UseQueryResult<PersonPaalaegDTO, Error> =>
  useQuery({
    queryKey: personPaalaegQueryKey(personId ?? "", paalaegId ?? ""),
    queryFn: () => getPersonPaalaeg(personId ?? "", paalaegId ?? ""),
    enabled: Boolean(personId) && Boolean(paalaegId),
  });

export interface CreatePersonPaalaegParams {
  personId: string;
  body: Omit<
    CreatePersonPaalaegBody,
    "decisionDocumentFileName" | "decisionDocumentContentType"
  >;
  file: File;
}

export const useCreatePersonPaalaeg = (): UseMutationResult<
  PersonPaalaegDTO,
  Error,
  CreatePersonPaalaegParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ personId, body, file }) => {
      const paalaeg = await createPersonPaalaeg(personId, {
        ...body,
        decisionDocumentFileName: file.name,
        decisionDocumentContentType: file.type || "application/pdf",
      });

      await createInjunctionDecision({
        userId: personId,
        externalInjunctionId: paalaeg.id,
        file,
      });

      return paalaeg;
    },
    onSuccess: (_data, { personId }) => {
      queryClient.invalidateQueries({
        queryKey: personPaalaegListQueryKey(personId),
      });
      queryClient.invalidateQueries({
        queryKey: ["cle-api", "users", personId, "injunction-decisions"],
      });
    },
  });
};

export interface UpdatePersonPaalaegParams {
  personId: string;
  paalaegId: string;
  changes: UpdatePersonPaalaegFields;
}

export const useUpdatePersonPaalaeg = (): UseMutationResult<
  PersonPaalaegDTO,
  Error,
  UpdatePersonPaalaegParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ personId, paalaegId, changes }) =>
      patchPersonPaalaeg(personId, paalaegId, getPatchOperations(changes)),
    onSuccess: (data, { personId, paalaegId }) => {
      queryClient.invalidateQueries({
        queryKey: personPaalaegListQueryKey(personId),
      });
      queryClient.invalidateQueries({
        queryKey: personPaalaegQueryKey(personId, paalaegId),
      });
      queryClient.invalidateQueries({
        queryKey: personPaalaegQueryKey(personId, data.id),
      });
    },
  });
};

export interface DeletePersonPaalaegParams {
  personId: string;
  paalaegId: string;
}

export const useDeletePersonPaalaeg = (): UseMutationResult<
  PersonPaalaegDTO,
  Error,
  DeletePersonPaalaegParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ personId, paalaegId }) =>
      deletePersonPaalaeg(personId, paalaegId),
    onSuccess: (_data, { personId, paalaegId }) => {
      queryClient.invalidateQueries({
        queryKey: personPaalaegListQueryKey(personId),
      });
      queryClient.invalidateQueries({
        queryKey: personPaalaegQueryKey(personId, paalaegId),
      });
    },
  });
};
